<script lang="ts">
    import PeoplePage from '@ProjectUsers/Pages/PeoplePage'
    export default PeoplePage
</script>

<template>
    <div>
        <ProjectUsersTagsManager :viewModel="projectUsersTagsManager"
                                  @create-tag="handleCreateProjectUsersTagCommand"
                                  @delete-tag="handleDeleteProjectUsersTagCommand"
                                  @update-tag="handleUpdateProjectUsersTagCommand"/>

        <ProjectUsersFromStringBuilder :viewModel="projectUsersFromStringBuilder"
                                       @create-project-users="handleCreateProjectUsersCommand" />

        <ProjectUserDeleter :viewModel="projectUserDeleter"
                            @delete-project-user="handleDeleteProjectUserCommand"
                            @archive-project-user="handleArchiveProjectUserCommand" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">People</h1>
                </div>
                <div class="col-lg-6 text-right">
                    <a class="btn btn-outline-secondary" v-sanitizedHref="`/#/assignments/${workspaceId}/${projectId}`" 
                       style="padding: 0.7em 2em;">Assign {{alternativesLabel}}</a>

                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="showTagsManager">Manage tags</button>
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showProjectUsersFromStringBuilder">Add</button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 60px;" />

        <div class="container">
            <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>
            <div v-if="initialized">

                <nav class="navbar navbar-light navbar-expand-md bg-light pl-1" role="navigation">
                    <div class="btn-group mr-auto" role="group">
                        <button type="button" class="btn btn-sm" @click="archivedFilter = false" :class="{ 'btn-secondary': !archivedFilter, 'btn-outline-secondary': archivedFilter }">
                            Active
                        </button>

                        <button type="button" class="btn btn-sm" @click="archivedFilter = true" :class="{ 'btn-secondary': archivedFilter, 'btn-outline-secondary': !archivedFilter }">
                            Archived
                        </button>
                    </div>

                    <form class="form-inline my-2 my-lg-0" role="search">
                        <input v-model="searchPhrase" class="form-control form-control-sm mr-sm-2" type="search" placeholder="Search">
                    </form>
                    <form class="form-inline">
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary  btn-sm dropdown-toggle" type="button" data-toggle="dropdown">
                                <span>Filter by: {{tagFilter ?? "Show all"}}</span>
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="tagFilter = null">Show all</a>
                                <h6 class="dropdown-header">By tag:</h6>
                                <a class="dropdown-item" v-for="t in tags" @click="tagFilter = t">{{t}}</a>
                            </div>
                        </div>
                    </form>
                </nav>

                <table class="table table-hover">
                    <tbody>
                        <tr v-for="p in projectUsersView" :key="p.ProjectUserId">
                            <td>
                                <div v-if="!p.Editing" class="d-flex flex-row">
                                    <div @click="p.edit()" class="pointer" style="width: 300px;">
                                        <span>{{p.Name}}</span><br />
                                        <small class="text-muted text-nowrap">
                                            <span v-if="p.IsUserAccepted" class="fa fa-user text-success" title="User"></span>
                                            <span v-if="p.IsUserRejected" class="fa fa-user text-danger" title="User access rejected"></span>
                                            <span v-if="p.IsUserRequested" class="fa fa-user text-secondary" title="User access requested"></span>
                                            <span>{{p.Email || 'No email'}}</span>
                                        </small>
                                    </div>
                                    <div @click="p.edit()" class="pointer">
                                        <span class="badge badge-secondary mr-1" v-for="t in p.Tags" :key="t">{{t}}</span>
                                    </div>
                                    <div class="ml-auto">
                                        <div class="dropdown" v-if="!p.IsArchived">
                                            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">Actions</button>
                                            <div class="dropdown-menu dropdown-menu-right">
                                                <a class="dropdown-item" @click="p.edit()">Edit</a>
                                                <div class="dropdown-divider"></div>

                                                <a v-if="!p.IsUserAccepted && !p.IsUserRequested && p.Email" class="dropdown-item" @click="handleRequestAccount(p)">Request an account</a>

                                                <a class="dropdown-item" @click="handleResetProjectUserAccessTokenCommand(p)">Reset access links</a>
                                                <a class="dropdown-item" @click="handleArchiveProjectUserCommand(p.ProjectUserId)">Archive</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" @click="showProjectUserDeleter(p)">Delete</a>
                                            </div>
                                        </div>
                                        <div class="dropdown" v-if="p.IsArchived">
                                            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">Actions</button>
                                            <div class="dropdown-menu dropdown-menu-right">
                                                <a class="dropdown-item" @click="handleUnarchiveProjectUserCommand(p)">Unarchive</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" @click="showProjectUserDeleter(p)">Delete</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="p.Editing" class="row">
                                    <div class="col-6">
                                        <div class="form-group">
                                            <label>Name</label>
                                            <input v-model.trim="p.NewName" type="text" class="form-control input-sm" placeholder="Name">
                                            <div class="error">{{p.NameError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-6">
                                        <div class="form-group">
                                            <label>Email</label>
                                            <input type="email" v-model.trim="p.NewEmail" class="form-control input-sm" placeholder="Email">
                                            <div class="error">{{p.EmailError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <label>Tags</label>
                                        <div class="dropdown d-inline-block">
                                            (<a class="text-primary" data-toggle="dropdown">add tag</a>)
                                            <div class="dropdown-menu">
                                                <a class="dropdown-item" v-for="t in tags" @click="p.addTag(t)">{{t}}</a>
                                            </div>
                                        </div><br />
                                        <span class="badge badge-secondary pointer mr-2" v-for="t in p.NewTags" :key="t" @click="p.removeTag(t)" title="remove a tag">{{t}} &times;</span>
                                    </div>

                                    <div class="col-12 text-right">
                                        <button @click="handleUpdateProjectUserCommand(p)" class="btn btn-primary btn-sm" title="confirm">
                                            <span class="fa fa-check"></span>
                                        </button>

                                        <button @click="p.cancelEdit()" class="btn btn-outline-secondary btn-sm" title="cancel">
                                            <span class="fa fa-times"></span>
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
